import type { Process } from "./process.types";
import type {
  AppSettings,
  AppSettingsRaw,
  IntegrationConfig,
  IntegrationToken,
  IntegrationConfigRaw,
  IntegrationTokenRaw,
  EmailTemplateRaw,
  EmailAttachmentRaw,
  ProcessKindRaw,
  ProcessStatusRaw,
  ProcessPDFTemplateRaw,
  AlertCategoryRaw,
  CommentFlagOptionRaw,
  UserGroupRaw,
  ObjectTypeWorkflowKindRelationRaw,
  BusinessUnitRaw,
  RegionRaw,
  ExtraJSONRaw,
  ObjectStatusRaw
} from "@certa/queries/types/appSettings.types";
import { StudioEnvBranchName } from "@certa/studio/src/constants/environmentBranches";
import type { DataObjectType } from "@certa/types/src/dataObjects.types";

export type StudioEnvironments =
  | "development"
  | "qa"
  | "staging"
  | "production";

export const studioEnvironmentMapping: Record<
  StudioEnvBranchName,
  StudioEnvironments
> = {
  [StudioEnvBranchName.DEVELOPMENT]: "development",
  [StudioEnvBranchName.QA]: "qa",
  [StudioEnvBranchName.STAGING]: "staging",
  [StudioEnvBranchName.PRODUCTION]: "production"
};

export type EnvBasedAppSettingsItems = {
  integrationToken: IntegrationToken[];
  integrationConfig: IntegrationConfig[];
};

export type EnvBasedAppSettingsItemsRaw = {
  integrationtoken: IntegrationTokenRaw[];
  integrationconfig: IntegrationConfigRaw[];
};

export type EnvBasedAppSettingsRaw = Record<
  StudioEnvironments,
  EnvBasedAppSettingsItemsRaw
>;

export type EnvBasedAppSettings = Record<
  StudioEnvironments,
  EnvBasedAppSettingsItems
>;

export type ReferencedAppSettings = Omit<AppSettings, "feExtraMetaData">;

export type ReferencedAppSettingsRaw = Omit<
  AppSettingsRaw,
  "frontend_extra_metadata" | "file_paths"
>;

export type ReferencedAppSettingsRawTypeMap = {
  emailtemplate: EmailTemplateRaw;
  emailattachment: EmailAttachmentRaw;
  businessunit: BusinessUnitRaw;
  region: RegionRaw;
  extrajson: ExtraJSONRaw;
  workflowkind: ProcessKindRaw;
  workflowstatus: ProcessStatusRaw;
  objectstatus: ObjectStatusRaw;
  workflowpdftemplate: ProcessPDFTemplateRaw;
  alertcategory: AlertCategoryRaw;
  commentflagoption: CommentFlagOptionRaw;
  group: UserGroupRaw;
  objecttypeworkflowkindrelation: ObjectTypeWorkflowKindRelationRaw;
  integrationconfig: IntegrationConfigRaw;
  integrationtoken: IntegrationTokenRaw;
};

export type BaseAppSettingsRaw = Omit<
  AppSettingsRaw,
  "frontend_extra_metadata" | "file_paths"
>;

export type BaseAppSettings = Omit<
  AppSettings,
  "feExtraMetaData" | "filePaths"
>;

export type MegaJSON = {
  id: string | null;
  processes: Record<string, Process>;
  base_app_settings: BaseAppSettingsRaw;
  app_settings: ReferencedAppSettingsRaw;
  records: Record<string, DataObjectType>;
  __appConfigSaveId: string; // UUID to track if app config is updated, doesn't update for processes change. They have their own __saveId
  __saveId: string;
};
